import { useMemo } from 'react';

import { type AddOnKey } from '@air/api';

import { useFetchAnyWorkspacePermissions } from '~/hooks/useFetchAnyWorkspacePermissions';
import { useCurrentWorkspaceAddOns } from '~/swr-hooks/workspaces/addOns/useCurrentWorkspaceAddOns';
import { canViewAddOns } from '~/utils/permissions/workspacePermissions';

// The AddOns API doesn't flag which add-ons are AI, so we enumerate the ones a workspace can turn off.
// semanticSearch is intentionally excluded — it's always on, so it can't distinguish AI on from off.
const AI_ADDON_KEYS: AddOnKey[] = [
  'ocr',
  'metadata',
  'transcribe',
  'facialRecognition',
  'videoIntelligence',
  'imageIntelligence',
  'documentIntelligence',
];

// Detects workspaces with AI totally disabled (every AI add-on off) so callers can fall back to plain
// "Storage" labeling. Defaults to "on" until we positively know otherwise, so we never mislabel a
// workspace as no-AI while permissions/data are still resolving.
export const useHasAnyAiAddons = (): boolean => {
  const { data: permissions } = useFetchAnyWorkspacePermissions();
  const { data: addOns } = useCurrentWorkspaceAddOns({ enabled: canViewAddOns(permissions) });

  return useMemo(() => {
    if (!addOns?.data) {
      return true;
    }

    return addOns.data.some((addOn) => AI_ADDON_KEYS.includes(addOn.key) && addOn.enabled);
  }, [addOns?.data]);
};
